import { getRealMimeType, convertExtensionsToMimeTypes } from '@/utils/handlers';
import { convertMbToKb } from '@/utils/format';

const SYNC_DIMENSIONS_TAGS = ['video'];
const EXTENDED_VALID_ELEMENTS_AUTOPLAY = 'video[*],iframe[*],source[*]';

const CONFIG_V5_EDITOR = {
    height: 500,
    content_style: 'p { margin: 0 },.tox .tox-statusbar{display: none}, body { overflow-y: auto !important; }',
    language: 'ja',
    language_url: '',
    content_css: '',
    menubar: true,
    automatic_uploads: true,
    importcss_append: false,
    disabled: false,
    statusbar: true,
    resize: true,
    is_autoresize_on: false,
    forced_root_block: '', // Tắt chế độ tự sinh thẻ <p>
    plugins: [
        'importcss',
        'hr advlist autolink lists link image charmap',
        'searchreplace visualblocks code fullscreen',
        'print preview anchor insertdatetime media template',
        'paste code help wordcount table code lists imagetools code'
    ],
    toolbar:
        'myCustomToolbarButton hr undo redo | forecolor backcolor | formatselect | bold italic | \
                                          alignleft aligncenter alignright | \
                                          link image media | \
                                          bullist numlist outdent indent | help code\
                                          table tableinsertdialog tablecellprops tableprops | fullscreen | code',
    table_toolbar:
        'tableprops tabledelete tablemergecells tablesplitcells | tableinsertrowbefore tableinsertrowafter tablerowprops tabledeleterow | tableinsertcolbefore tableinsertcolafter tablecellprops tabledeletecol',
    images_upload_url: '', // Your upload URL
    media_live_embeds: true
};

const CONFIG_LAST_EDITOR = {
    height: 500,
    content_style: 'body { white-space: pre-wrap; overflow-y: auto !important; } p, div { margin: 0; },.tox .tox-statusbar{display: none}',
    entity_encoding: 'raw',
    language: 'ja',
    language_url: '',
    content_css: '',
    disabled: false,
    menubar: true,
    automatic_uploads: true,
    importcss_append: false,
    statusbar: true,
    resize: true,
    is_autoresize_on: false,
    forced_root_block: 'div', // Tắt chế độ tự sinh thẻ <p>
    plugins:
        'importcss advlist autolink lists link image charmap searchreplace visualblocks code fullscreen  preview anchor insertdatetime media code help wordcount table code lists  code',
    toolbar:
        'myCustomToolbarButton hr undo redo | forecolor backcolor | formatselect | bold italic | \
                                          alignleft aligncenter alignright | \
                                          link image media | \
                                          bullist numlist outdent indent | help code\
                                          table tableinsertdialog tablecellprops tableprops | fullscreen | code',
    table_toolbar:
        'tableprops tabledelete tablemergecells tablesplitcells | tableinsertrowbefore tableinsertrowafter tablerowprops tabledeleterow | tableinsertcolbefore tableinsertcolafter tablecellprops tabledeletecol',
    images_upload_url: '', // Your upload URL
    media_live_embeds: true,
    file_picker_types: 'media',
    limit_size_video_mb: 50,
    allowed_extensions: ['mp4', 'mov', 'wmv'],
    extended_valid_elements: EXTENDED_VALID_ELEMENTS_AUTOPLAY
};

const CONFIG_MORE = {
    configModalProgress: {
        title: '',
        model: false,
        hideFooter: true,
        size: 'md',
        centered: true,
        noCloseOnBackdrop: true,
        hideCloseButton: true,
        class: 'modal-video-progress'
    }
};

const VIDEO_TEMPLATE_AUTOPLAY = (data) => {
    return `<video width="${data.width}" height="${data.height}"${data.poster ? ` poster="${data.poster}"` : ''} controls autoplay muted playsinline>
<source src="${data.source}"${data.sourcemime ? ` type="${data.sourcemime}"` : ''} />
${data.altsource ? `<source src="${data.altsource}"${data.altsourcemime ? ` type="${data.altsourcemime}"` : ''} />` : ''}
</video>`;
};

const IFRAME_TEMPLATE_AUTOPLAY = (data) => {
    let src = data.source;
    if (src.includes('youtube.com') || src.includes('youtu.be')) {
        if (src.includes('?')) {
            if (!src.includes('autoplay=1')) src += '&autoplay=1';
            if (!src.includes('mute=1')) src += '&mute=1';
        } else {
            src += '?autoplay=1&mute=1';
        }
    }
    return `<iframe src="${src}" width="${data.width}" height="${data.height}" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
};

const validateVideoFile = async (file, configEditor) => {
    const allowedExtensions = configEditor.allowed_extensions || [];
    const allowedMimes = convertExtensionsToMimeTypes(allowedExtensions);
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const fileMime = await getRealMimeType(file);

    if (!allowedExtensions.includes(fileExtension) || !allowedMimes.includes(fileMime)) {
        return { isValid: false, errorType: 'FORMAT' };
    }

    const maxSizeMB = configEditor.limit_size_video_mb;
    if (file.size / 1024 > convertMbToKb(maxSizeMB)) {
        return { isValid: false, errorType: 'SIZE', maxSizeMB };
    }

    return { isValid: true };
};

const fixVideoSize = (node) => {
    if (!node) return;
    let videos = [];
    if (node.tagName === 'VIDEO') {
        videos.push(node);
    } else if (typeof node.querySelectorAll === 'function') {
        videos = Array.from(node.querySelectorAll('video'));
    }
    videos.forEach((video) => {
        const w = video.getAttribute('width');
        const h = video.getAttribute('height');
        if (w && h) {
            const targetWidth = w.includes('px') || w.includes('%') ? w : w + 'px';
            const targetHeight = h.includes('px') || h.includes('%') ? h : h + 'px';
            video.setAttribute('style', `width:${targetWidth}; height:${targetHeight};`);
        }
    });
};

const registerSyncFilters = (editor, isAutoplay = false) => {
    const fixNodes = (nodes) => {
        nodes.forEach((node) => {
            if (node.name === 'video' && isAutoplay) {
                node.attr('autoplay', 'autoplay');
                node.attr('muted', 'muted');
                node.attr('playsinline', 'true');
            }

            const style = node.attr('style') || '';
            const wMatch = style.match(/width:\s*([\d.]+)px/);
            const hMatch = style.match(/height:\s*([\d.]+)px/);

            let w = wMatch ? wMatch[1] : node.attr('width');
            let h = hMatch ? hMatch[1] : node.attr('height');

            if (w && h) {
                node.attr('width', Math.round(parseFloat(w)).toString());
                node.attr('height', Math.round(parseFloat(h)).toString());

                const targetWidth = w.toString().includes('px') || w.toString().includes('%') ? w : w + 'px';
                const targetHeight = h.toString().includes('px') || h.toString().includes('%') ? h : h + 'px';
                node.attr('style', `width:${targetWidth}; height:${targetHeight};`);
            }
        });
    };
    const tags = SYNC_DIMENSIONS_TAGS.join(',');
    editor.parser.addNodeFilter(tags, fixNodes);
    editor.serializer.addNodeFilter(tags, fixNodes);
};

const handleObjectResized = (e) => {
    const target = e.target;
    if (!target) return;
    const tagName = target.tagName.toLowerCase();
    const selector = SYNC_DIMENSIONS_TAGS.join(',');
    const el = SYNC_DIMENSIONS_TAGS.includes(tagName) ? target : target.querySelector(selector);
    if (el) {
        const w = target.style.width;
        const h = target.style.height;
        if (w) el.setAttribute('width', Math.round(parseFloat(w)));
        if (h) el.setAttribute('height', Math.round(parseFloat(h)));
        fixVideoSize(el);
    }
};

export {
    CONFIG_V5_EDITOR,
    CONFIG_LAST_EDITOR,
    EXTENDED_VALID_ELEMENTS_AUTOPLAY,
    CONFIG_MORE,
    SYNC_DIMENSIONS_TAGS,
    VIDEO_TEMPLATE_AUTOPLAY,
    IFRAME_TEMPLATE_AUTOPLAY,
    validateVideoFile,
    fixVideoSize,
    registerSyncFilters,
    handleObjectResized
};
